import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { Badge } from 'components/ui/badge';
import { 
  ArrowLeft, MessageSquare, Phone, Bell, Check, Loader2,
  Send, Clock, Calendar, DollarSign, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

export default function SMSSettings() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [smsStatus, setSmsStatus] = useState({ configured: false });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [preferences, setPreferences] = useState({
    shift_reminders: true,
    time_off_updates: true,
    caregiver_requests: true,
    clock_notifications: true,
    budget_alerts: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statusRes, prefsRes] = await Promise.all([
        api.get('/sms/status'),
        api.get('/sms/preferences')
      ]);
      
      setSmsStatus(statusRes.data);
      setPreferences(prefsRes.data.preferences || preferences);
      setPhoneNumber(user?.phone || '');
      setTestPhone(user?.phone || '');
    } catch (error) {
      console.error('Failed to fetch SMS settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await api.put('/sms/preferences', preferences);
      toast.success('SMS preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePhone = async () => {
    if (!phoneNumber) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/sms/phone', { phone_number: phoneNumber });
      toast.success('Phone number updated');
    } catch (error) {
      toast.error('Failed to update phone number');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setSending(true);
    try {
      await api.post('/sms/test', { phone_number: testPhone });
      toast.success('Test SMS sent! Check your phone.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setSending(false);
    }
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const notificationTypes = [
    { 
      key: 'shift_reminders', 
      icon: Clock, 
      title: 'Shift Reminders', 
      description: 'Get reminded before your scheduled shifts'
    },
    { 
      key: 'time_off_updates', 
      icon: Calendar, 
      title: 'Time-Off Updates', 
      description: 'Notifications when time-off requests are submitted or responded to'
    },
    { 
      key: 'caregiver_requests', 
      icon: UserPlus, 
      title: 'Caregiver Requests', 
      description: 'Alerts when families request your caregiving services'
    },
    { 
      key: 'clock_notifications', 
      icon: Bell, 
      title: 'Clock In/Out', 
      description: 'Notifications when caregivers clock in or out'
    },
    { 
      key: 'budget_alerts', 
      icon: DollarSign, 
      title: 'Budget Alerts', 
      description: 'Warnings when budget thresholds are reached'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-teal-500" />
                SMS Notifications
              </h1>
              <p className="text-xs sm:text-sm text-slate-500">Manage your SMS notification preferences</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 sm:py-8 space-y-4 sm:space-y-6">
        {/* SMS Status */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  smsStatus.configured ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  <MessageSquare className={`w-5 h-5 ${
                    smsStatus.configured ? 'text-green-600' : 'text-amber-600'
                  }`} />
                </div>
                <div>
                  <p className="font-medium text-slate-900">SMS Service Status</p>
                  <p className="text-sm text-slate-500">{smsStatus.message}</p>
                </div>
              </div>
              <Badge className={smsStatus.configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                {smsStatus.configured ? 'Active' : 'Not Configured'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Phone Number */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-teal-500" />
              Your Phone Number
            </CardTitle>
            <CardDescription>Enter your phone number to receive SMS notifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Input
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="flex-1"
                data-testid="phone-input"
              />
              <Button onClick={handleUpdatePhone} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Enter your phone number in E.164 format (e.g., +15551234567) or just the digits
            </p>
          </CardContent>
        </Card>

        {/* Test SMS */}
        {smsStatus.configured && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-teal-500" />
                Send Test SMS
              </CardTitle>
              <CardDescription>Verify your SMS setup by sending a test message</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <Input
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="flex-1"
                  data-testid="test-phone-input"
                />
                <Button 
                  onClick={handleSendTest} 
                  disabled={sending}
                  className="bg-teal-500 hover:bg-teal-600"
                  data-testid="send-test-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send Test
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notification Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-teal-500" />
              Notification Preferences
            </CardTitle>
            <CardDescription>Choose which notifications you want to receive via SMS</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {notificationTypes.map(({ key, icon: Icon, title, description }) => (
              <div key={key} className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 text-sm">{title}</p>
                    <p className="text-xs sm:text-sm text-slate-500 leading-tight">{description}</p>
                  </div>
                </div>
                <Switch
                  checked={preferences[key]}
                  onCheckedChange={() => togglePreference(key)}
                  data-testid={`toggle-${key}`}
                />
              </div>
            ))}
            
            <Button 
              onClick={handleSavePreferences} 
              disabled={saving}
              className="w-full bg-teal-500 hover:bg-teal-600"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Preferences
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
